import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import { getPendingRequests } from './functions/get_pending_requests.function';
import { enrichPendingRequestsWithUsers } from './functions/enrich_pending_requests_with_users.function';
import { getAllUsers } from '../SystemRoleAssignment/functions/get_all_users.function';
import { getAllRoles } from '../SystemRoleAssignment/functions/get_all_roles.function';
import { approveAssignmentRequest } from './functions/approve_request.function';
import { rejectAssignmentRequest } from './functions/reject_request.function';
import styles from '../RoleAssignment/role_assignment.module.css';
import { getRoleAssignmentTableBaseOptions } from '../RoleAssignment/role_assignment_table.utils';
import { RoleAssignmentTableShell } from '../RoleAssignment/role_assignment_table_shell.component';
import { RoleAssignmentSearchToolbar } from '../RoleAssignment/role_assignment_search_toolbar.component';

const actionColor = {
  CREATE: 'success',
  UPDATE: 'warning',
  DELETE: 'error',
};

const cellText = (value, muted = false) => (
  <span style={{ fontSize: '14px', color: muted ? '#667085' : '#1D242E' }}>{value || 'N/A'}</span>
);

export const SystemInvoiceApproverPendingApprovals = ({ onProcessed }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [reviewOpen, setReviewOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [reviewAction, setReviewAction] = useState('approve');
  const [reviewComment, setReviewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchPending = useCallback(async () => {
    setLoading(true);
    const [pendingData, usersResult, rolesResult] = await Promise.all([
      getPendingRequests(),
      getAllUsers(),
      getAllRoles(),
    ]);
    const users = usersResult?.response || usersResult?.data || [];
    const roles = rolesResult?.response || rolesResult?.data || [];
    const enriched = enrichPendingRequestsWithUsers(
      Array.isArray(pendingData) ? pendingData : [],
      users,
      roles
    );
    setRows(enriched);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchPending();
  }, [fetchPending]);

  const filteredRows = useMemo(() => {
    if (!searchTerm) {
      return rows;
    }
    const q = searchTerm.toLowerCase();
    return rows.filter((row) =>
      Object.values(row).some((val) =>
        String(val ?? '')
          .toLowerCase()
          .includes(q)
      )
    );
  }, [rows, searchTerm]);

  const openReview = (row, action) => {
    setSelectedRow(row);
    setReviewAction(action);
    setReviewComment('');
    setReviewOpen(true);
  };

  const handleConfirmReview = async () => {
    if (!selectedRow) {
      return;
    }
    setSubmitting(true);
    const fn = reviewAction === 'approve' ? approveAssignmentRequest : rejectAssignmentRequest;
    const result = await fn(selectedRow.id, reviewComment);
    setSubmitting(false);
    if (result.success) {
      setReviewOpen(false);
      await fetchPending();
      if (onProcessed) {
        onProcessed();
      }
    }
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: 'action_type',
        header: 'Action',
        size: 120,
        enableColumnActions: false,
        Cell: ({ cell }) => (
          <Chip
            label={cell.getValue()}
            size="small"
            color={actionColor[cell.getValue()] || 'default'}
          />
        ),
      },
      {
        accessorKey: 'role_name',
        header: 'Levels',
        size: 200,
        enableColumnActions: false,
        Cell: ({ cell }) => cellText(cell.getValue()),
      },
      {
        accessorKey: 'user_name',
        header: 'New user name',
        size: 160,
        enableColumnActions: false,
        Cell: ({ cell }) => cellText(cell.getValue()),
      },
      {
        accessorKey: 'user_email',
        header: 'New user email',
        size: 220,
        enableColumnActions: false,
        Cell: ({ cell }) => cellText(cell.getValue(), true),
      },
      {
        accessorKey: 'previous_user_name',
        header: 'Previous user name',
        size: 160,
        enableColumnActions: false,
        Cell: ({ cell }) => cellText(cell.getValue(), true),
      },
      {
        accessorKey: 'previous_user_email',
        header: 'Previous user email',
        size: 220,
        enableColumnActions: false,
        Cell: ({ cell }) => cellText(cell.getValue(), true),
      },
      {
        accessorKey: 'requested_by_display',
        header: 'Requested by',
        size: 150,
        enableColumnActions: false,
        Cell: ({ cell }) => cellText(cell.getValue(), true),
      },
      {
        accessorKey: 'requested_at',
        header: 'Requested at',
        size: 180,
        enableColumnActions: false,
        Cell: ({ cell }) =>
          cellText(cell.getValue() ? new Date(cell.getValue()).toLocaleDateString() : '—', true),
      },
      {
        accessorKey: 'request_comment',
        header: 'Comment',
        size: 200,
        enableColumnActions: false,
        Cell: ({ cell }) => cellText(cell.getValue(), true),
      },
    ],
    []
  );

  const table = useMaterialReactTable({
    ...getRoleAssignmentTableBaseOptions(),
    columns,
    data: filteredRows,
    state: { isLoading: loading },
    enableSorting: false,
    enableRowActions: true,
    positionActionsColumn: 'last',
    renderTopToolbarCustomActions: () => (
      <RoleAssignmentSearchToolbar searchTerm={searchTerm} onSearchChange={setSearchTerm} />
    ),
    renderRowActions: ({ row }) => (
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button
          size="small"
          variant="contained"
          sx={{
            backgroundColor: '#12B76A',
            textTransform: 'none',
            fontSize: '13px',
            boxShadow: 'none',
            '&:hover': { backgroundColor: '#0E9F5A', boxShadow: 'none' },
          }}
          onClick={() => openReview(row.original, 'approve')}
        >
          Approve
        </Button>
        <Button
          size="small"
          variant="outlined"
          sx={{
            color: '#BD1D23',
            borderColor: '#BD1D23',
            textTransform: 'none',
            fontSize: '13px',
            '&:hover': { borderColor: '#9a171c', backgroundColor: 'rgba(189, 29, 35, 0.04)' },
          }}
          onClick={() => openReview(row.original, 'reject')}
        >
          Reject
        </Button>
      </Box>
    ),
  });

  return (
    <div className={styles['role-assignment-container']} style={{ padding: 0 }}>
      <h2 className={styles['invoice-approver-section-title']}>Pending assignment changes</h2>
      <RoleAssignmentTableShell>
        <MaterialReactTable table={table} />
      </RoleAssignmentTableShell>
      <Dialog
        open={reviewOpen}
        onClose={() => !submitting && setReviewOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px' } }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          {reviewAction === 'approve' ? 'Approve' : 'Reject'} request
        </DialogTitle>
        <DialogContent>
          {selectedRow && (
            <Box sx={{ mb: 2, color: '#667085', fontSize: '14px' }}>
              <Typography variant="body2" sx={{ mb: 1.5, color: '#1D242E', fontWeight: 500 }}>
                {selectedRow.action_type} — {selectedRow.role_name}
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>New user:</strong> {selectedRow.user_name || 'N/A'}
                {selectedRow.user_email ? ` (${selectedRow.user_email})` : ''}
              </Typography>
              {(selectedRow.previous_user_id || selectedRow.previous_user_name) && (
                <Typography variant="body2">
                  <strong>Previous user:</strong> {selectedRow.previous_user_name || 'N/A'}
                  {selectedRow.previous_user_email ? ` (${selectedRow.previous_user_email})` : ''}
                </Typography>
              )}
            </Box>
          )}
          <TextField
            label="Review comment (optional)"
            fullWidth
            multiline
            minRows={2}
            value={reviewComment}
            onChange={(e) => setReviewComment(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setReviewOpen(false)}
            disabled={submitting}
            sx={{ color: '#667085' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            sx={{
              backgroundColor: reviewAction === 'approve' ? '#12B76A' : '#BD1D23',
              textTransform: 'none',
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: reviewAction === 'approve' ? '#0E9F5A' : '#9a171c',
                boxShadow: 'none',
              },
            }}
            onClick={handleConfirmReview}
            disabled={submitting}
          >
            {submitting ? 'Saving...' : reviewAction === 'approve' ? 'Approve' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
